import { Children, isValidElement, type ReactElement, type ReactNode } from 'react';
import { useExperimentVariant } from '../../hooks/experiment/useExperimentVariant';
import { EXPERIMENTS } from '../../constants/experiment';
import type { ExperimentName } from '../../constants/experiment';
import { VARIANT_SYMBOL } from './ExperimentVariant';

interface ExperimentRootProps {
  name: ExperimentName;
  fallback?: ReactNode;
  children: ReactNode;
}

export function ExperimentRoot({ name, fallback, children }: ExperimentRootProps) {
  const { variant, isLoading } = useExperimentVariant(name);

  if (isLoading && fallback !== undefined) {
    return <>{fallback}</>;
  }

  const match = findMatchingVariant(children, variant);

  if (!match) {
    const defaultVariant = EXPERIMENTS[name].default;
    const defaultMatch = findMatchingVariant(children, defaultVariant);
    return defaultMatch ?? null;
  }

  return match;
}

function isVariantElement(child: ReactElement): child is ReactElement<{ name: string }> {
  return (
    (child.type as { __experimentVariant?: symbol }).__experimentVariant === VARIANT_SYMBOL
  );
}

function findMatchingVariant(children: ReactNode, variantName: string): ReactElement | null {
  let match: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (isValidElement(child) && isVariantElement(child) && child.props.name === variantName) {
      match = child;
    }
  });

  return match;
}
